'use client'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import Pagination from '../common/Pagination'
import ResourceCardSkeleton from './ResourceCardSkeleton'
import usePagination from '@/hooks/usePagination'
import { PrismicLink, PrismicRichText } from '@prismicio/react'
import { RichTextField, ImageField, LinkField } from '@prismicio/client'

interface ResourceItem {
  title?: RichTextField | null
  image?: ImageField | null
  link?: LinkField | null
  type?: string
  [key: string]: unknown
}

type ResourcesContentProps = {
  filterResults: ResourceItem[]
  showNoData: boolean
  itemsPerPage?: number
  scrollToTop?: number
  scrollToLeft?: number
  showPagination: boolean
}

const PAGINATION_SKELETON_MS = 550

const CARD_HOVER_CLASS =
  'origin-center rounded-md border border-primary-ink-6 shadow-light backdrop-blur-25 transition-transform duration-300 ease-out group-hover:scale-[1.01] motion-reduce:transition-none motion-reduce:group-hover:scale-100'

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Helper function to determine if a URL should be treated as internal navigation
 * Converts same-domain Web links to internal paths
 */
const processLink = (link: LinkField | null | undefined) => {
  if (!link) return { isExternal: false, url: null, isValid: false }

  const linkType = link.link_type

  // Handle empty link (link_type is 'Any' for empty links)
  if (linkType === 'Any') {
    return { isExternal: false, url: null, isValid: false }
  }

  // Handle Document type (Prismic internal links)
  if (linkType === 'Document') {
    const docLink = link as { id?: string; uid?: string; link_type: 'Document' }
    return {
      isExternal: false,
      url: null,
      isValid: !!(docLink.id || docLink.uid),
      usePrismicLink: true,
    }
  }

  // Handle Web/Media types
  if (linkType === 'Web' || linkType === 'Media') {
    const webLink = link as { url?: string; target?: string; link_type: 'Web' | 'Media' }
    const linkUrl = webLink.url

    if (!linkUrl) {
      return { isExternal: false, url: null, isValid: false }
    }

    try {
      const url = new URL(linkUrl)
      const isSameDomain = url.hostname === 'secureframe.com' || url.hostname === 'www.secureframe.com'

      if (isSameDomain) {
        // Convert same-domain URLs to internal navigation
        return {
          isExternal: false,
          url: url.pathname + url.search + url.hash,
          isValid: true,
          usePrismicLink: false,
        }
      }

      // True external URL
      return {
        isExternal: true,
        url: linkUrl,
        isValid: true,
        target: webLink.target || '_blank',
      }
    } catch {
      // Invalid URL
      return { isExternal: false, url: null, isValid: false }
    }
  }

  return { isExternal: false, url: null, isValid: false }
}

const ResourcesContent = ({
  filterResults,
  showNoData,
  showPagination,
  itemsPerPage = 9,
  scrollToTop = 800,
  scrollToLeft = 0,
}: ResourcesContentProps) => {
  const { currentData, currentPage, maxPage, goToPage, nextPage, prevPage } = usePagination(
    filterResults,
    itemsPerPage,
    scrollToTop,
    scrollToLeft,
  )
  const [isPaginating, setIsPaginating] = useState(false)
  const skeletonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (skeletonTimeoutRef.current) clearTimeout(skeletonTimeoutRef.current)
    }
  }, [])

  const withPaginationSkeleton = useCallback((action: () => void) => {
    if (prefersReducedMotion()) {
      action()
      return
    }

    if (skeletonTimeoutRef.current) clearTimeout(skeletonTimeoutRef.current)
    setIsPaginating(true)
    action()
    skeletonTimeoutRef.current = setTimeout(() => {
      setIsPaginating(false)
      skeletonTimeoutRef.current = null
    }, PAGINATION_SKELETON_MS)
  }, [])

  const handleGoToPage = (page: number) => {
    if (page === currentPage) return
    withPaginationSkeleton(() => goToPage(page))
  }

  const handleNextPage = () => {
    if (currentPage >= maxPage) return
    withPaginationSkeleton(nextPage)
  }

  const handlePrevPage = () => {
    if (currentPage <= 1) return
    withPaginationSkeleton(prevPage)
  }

  const skeletonCount = currentData.length || itemsPerPage

  return (
    <div className="lg:col-span-6">
      {isPaginating ? (
        <p className="sr-only" role="status" aria-live="polite">
          Loading resources...
        </p>
      ) : null}

      {showNoData ? (
        <div className="mx-auto flex h-[400px] max-w-xl flex-col items-center justify-center text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="80"
            height="80"
            fill="#e1e1f4"
            viewBox="0 0 256 256"
            role="none">
            <path d="M165.66,101.66,139.31,128l26.35,26.34a8,8,0,0,1-11.32,11.32L128,139.31l-26.34,26.35a8,8,0,0,1-11.32-11.32L116.69,128,90.34,101.66a8,8,0,0,1,11.32-11.32L128,116.69l26.34-26.35a8,8,0,0,1,11.32,11.32ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
          </svg>
          <h3>No results found. Please try adjusting the filters or searching by another keyword.</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" aria-busy={isPaginating}>
          {isPaginating
            ? Array.from({ length: skeletonCount }, (_, index) => <ResourceCardSkeleton key={`skeleton-${index}`} />)
            : currentData.map((resource, index) => {
                const key = `i-${index}`
                const linkInfo = processLink(resource.link)
                const resourceTitle =
                  (resource.title?.[0] as { text?: string } | undefined)?.text || 'Resource image'

                const cardContent = (
                  <div className={CARD_HOVER_CLASS}>
                    <div className="relative h-[200px] w-full overflow-hidden rounded-t-md">
                      <Image
                        src={resource.image?.url || '/images/placeholder.png'}
                        alt={resourceTitle}
                        fill
                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                        className="object-cover object-center"
                      />
                    </div>

                    <div className="flex flex-col gap-4 bg-white/30 p-5 lg:px-5 lg:pb-5 lg:pt-4">
                      <div className="min-h-[130px]">
                        <div className="mb-4">
                          <span className="bg-secondary-gray-150 p-[8px] font-halyardDisplay text-sm">
                            {resource.type}
                          </span>
                        </div>

                        <div className="[&>h3]:text-[22px] [&>h3]:tracking-wide [&>h3]:transition-colors [&>h3]:duration-300 group-hover:[&>h3]:text-primary-green-85 motion-reduce:[&>h3]:transition-none">
                          <PrismicRichText field={resource.title} />
                        </div>
                      </div>

                      <div className="btn-interaction flex !w-fit !p-0">
                        <span className="mr-1.5">Read on</span>
                        <Image src="/images/angle-right-green.svg" width={16} height={9} alt="" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                )

                // External URLs (true external, not same-domain)
                if (linkInfo.isValid && linkInfo.isExternal) {
                  return (
                    <a
                      href={linkInfo.url!}
                      target={linkInfo.target}
                      rel="noopener noreferrer"
                      className="group block"
                      key={key}>
                      {cardContent}
                    </a>
                  )
                }

                // Internal Next.js navigation (converted same-domain URLs)
                if (linkInfo.isValid && !linkInfo.isExternal && linkInfo.url) {
                  return (
                    <Link href={linkInfo.url} className="group block" key={key}>
                      {cardContent}
                    </Link>
                  )
                }

                // Prismic Document links
                if (linkInfo.isValid && linkInfo.usePrismicLink) {
                  return (
                    <PrismicLink field={resource.link} className="group block" key={key}>
                      {cardContent}
                    </PrismicLink>
                  )
                }

                // Invalid or missing link - non-clickable
                return (
                  <div className="cursor-not-allowed opacity-60" key={key}>
                    {cardContent}
                  </div>
                )
              })}
        </div>
      )}

      {showPagination && maxPage > 1 && (
        <Pagination
          currentPage={currentPage}
          maxPage={maxPage}
          goToPage={handleGoToPage}
          nextPage={handleNextPage}
          prevPage={handlePrevPage}
        />
      )}
    </div>
  )
}

export default ResourcesContent
